import type { Breadcrumb, ErrorEvent, EventHint } from "@sentry/react";
import { normalizeAppBrokenRoute } from "./routePattern";

/**
 * Telemetry for the failure mode where a `<script>`, `<link rel=modulepreload>` or dynamic
 * `import()` is answered with something that is not JavaScript.
 *
 * The browser rejects the response before any of our code runs, so the thrown error has no
 * stack, no filename and no reference to the asset that failed — Safari's
 * `TypeError: 'text/html' is not a valid JavaScript MIME type.` is the canonical example
 * (Sentry TEACH-28QJ: 849 events, zero frames, nothing to act on). Sentry's global handler
 * still reports it, so the gap is context, not capture: nothing in the app records which URL
 * was being loaded, what the server actually answered with, or which page the person was on
 * (the `url` tag collapses to the origin on the hash-routed apps).
 *
 * This module fills that in from three cheap sources that need no extra network requests:
 * a capture-phase resource-error listener, Vite's `vite:preloadError` event, and the
 * PerformanceResourceTiming entries the browser already recorded for the asset requests.
 */

export const ASSET_LOAD_ERROR_TAG = "asset_load_error";
export const ASSET_LOAD_MIME_TYPE_TAG = "asset_load_mime_type";
export const ASSET_LOAD_ROUTE_TAG = "asset_load_route";
export const ASSET_LOAD_CONTEXT_KEY = "asset_load";

export const ASSET_LOAD_BREADCRUMB_CATEGORY = "asset.load";

/**
 * How the asset load failed, as far as the message tells us.
 *
 * - `invalid_mime_type`: the response arrived but was not JavaScript. A same-origin `/assets/`
 *   request answered with `text/html` means an HTML fallback (SPA index, edge error page) was
 *   served for a chunk URL — usually a hash that no longer exists after a deploy.
 * - `module_fetch_failed`: the module request itself failed (network, 4xx/5xx, blocked).
 * - `chunk_load_failed`: the legacy bundler-level chunk error.
 */
export type AssetLoadErrorKind = "invalid_mime_type" | "module_fetch_failed" | "chunk_load_failed";

// Wording differs per engine for the same failure, so match all of them rather than the one
// signature that happens to dominate an issue today.
const INVALID_MIME_TYPE_PATTERNS: RegExp[] = [
  // Safari / WebKit — the TEACH-28QJ signature. Captures the MIME type it was given.
  /['"]([^'"]+)['"] is not a valid (?:JavaScript|JS) MIME type/i,
  // Chromium
  /Expected a JavaScript(?:-or-Wasm)? module script but the server responded with a MIME type of ["']?([^"'.]+)/i,
  /Refused to execute script .*because its MIME type \(['"]?([^'")]+)/i,
  // Firefox
  /Loading module .*was blocked because of a disallowed MIME type \(["']?([^"')]+)/i,
];

const MODULE_FETCH_FAILED_PATTERNS: RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS for/i,
];

const CHUNK_LOAD_FAILED_PATTERNS: RegExp[] = [/^ChunkLoadError$/, /Loading chunk \S+ failed/];

export type AssetLoadErrorClassification = {
  kind: AssetLoadErrorKind;
  /** The MIME type the server answered with, when the message names one. */
  mimeType?: string;
};

/**
 * Classify a thrown message as an asset-load failure, or `undefined` when it is not one.
 * Deliberately message-only: these errors carry nothing else to key off.
 */
export const classifyAssetLoadError = (message: string | undefined): AssetLoadErrorClassification | undefined => {
  if (!message) return undefined;

  for (const pattern of INVALID_MIME_TYPE_PATTERNS) {
    const match = pattern.exec(message);
    if (match) {
      return { kind: "invalid_mime_type", ...(match[1] ? { mimeType: match[1] } : {}) };
    }
  }

  if (MODULE_FETCH_FAILED_PATTERNS.some((pattern) => pattern.test(message))) {
    return { kind: "module_fetch_failed" };
  }

  if (CHUNK_LOAD_FAILED_PATTERNS.some((pattern) => pattern.test(message))) {
    return { kind: "chunk_load_failed" };
  }

  return undefined;
};

export type FailedAsset = {
  /** Path plus search only: asset URLs are ours, but keep the shape uniform with the timings below. */
  url: string;
  /** `script`, `link` (preload / stylesheet), or `preload-helper` for Vite's own event. */
  source: string;
  /** `rel` for a `<link>`, `type` for a `<script>`. */
  detail?: string;
  /** Milliseconds since page start, so a burst can be lined up against a deploy. */
  atMs: number;
};

// A page that loses one chunk usually loses several. Keep the last few and drop the rest:
// enough to see the pattern, small enough to stay inside a Sentry context.
const MAX_TRACKED_FAILED_ASSETS = 5;
const MAX_REPORTED_ASSET_TIMINGS = 5;

const failedAssets: FailedAsset[] = [];

export const now = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function" ? Math.round(performance.now()) : 0;

/** Absolute URL reduced to path + search. Keeps host noise and any signed-URL query out of tags. */
const toRelativeUrl = (url: string): string => {
  try {
    const parsed = new URL(url, typeof window === "undefined" ? undefined : window.location.href);
    return `${parsed.pathname}${parsed.search}`;
    // eslint-disable-next-line no-catch-all/no-catch-all -- a URL we cannot parse is still worth reporting verbatim
  } catch {
    return url;
  }
};

export const recordFailedAsset = (asset: Omit<FailedAsset, "atMs">): FailedAsset => {
  const recorded: FailedAsset = { ...asset, url: toRelativeUrl(asset.url), atMs: now() };

  failedAssets.push(recorded);
  if (failedAssets.length > MAX_TRACKED_FAILED_ASSETS) failedAssets.shift();

  return recorded;
};

export const getRecordedFailedAssets = (): FailedAsset[] => [...failedAssets];

/** Test seam: the buffer is module state that outlives a single test. */
export const resetRecordedFailedAssets = (): void => {
  failedAssets.length = 0;
};

type AssetTiming = {
  url: string;
  /** HTTP status, when the browser exposes it (Safari 16.4+, Chromium 109+). */
  status?: number;
  /** Bytes on the wire. An HTML fallback served for a chunk is far smaller than the chunk. */
  encodedBodySize?: number;
  /** `cache`, `navigational-prefetch`, or empty for a normal network fetch. */
  deliveryType?: string;
  atMs: number;
};

const isScriptOrStyleInitiator = (entry: PerformanceResourceTiming): boolean =>
  entry.initiatorType === "script" || entry.initiatorType === "link" || entry.initiatorType === "css";

/**
 * The most recent script/stylesheet requests the browser recorded.
 *
 * This is what makes the failing URL recoverable when nothing fired a resource `error` event:
 * a dynamic `import()` rejected on MIME type still leaves a timing entry, and its
 * `encodedBodySize` distinguishes "chunk is gone, HTML fallback served" from "chunk is there".
 */
export const getRecentAssetTimings = (limit = MAX_REPORTED_ASSET_TIMINGS): AssetTiming[] => {
  if (typeof performance === "undefined" || typeof performance.getEntriesByType !== "function") return [];

  try {
    const entries = performance.getEntriesByType("resource") as PerformanceResourceTiming[];

    return entries
      .filter(isScriptOrStyleInitiator)
      .slice(-limit)
      .map((entry) => {
        const status = (entry as PerformanceResourceTiming & { responseStatus?: number }).responseStatus;
        const deliveryType = (entry as PerformanceResourceTiming & { deliveryType?: string }).deliveryType;

        return {
          url: toRelativeUrl(entry.name),
          ...(typeof status === "number" ? { status } : {}),
          ...(typeof entry.encodedBodySize === "number" ? { encodedBodySize: entry.encodedBodySize } : {}),
          ...(deliveryType ? { deliveryType } : {}),
          atMs: Math.round(entry.startTime),
        };
      });
    // eslint-disable-next-line no-catch-all/no-catch-all -- diagnostics must never throw out of beforeSend
  } catch {
    return [];
  }
};

/** The module entry point the document shipped, so a stale/rewritten asset base is visible. */
const getEntryModuleUrl = (): string | undefined => {
  if (typeof document === "undefined") return undefined;

  const entry = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
  return entry?.src ? toRelativeUrl(entry.src) : undefined;
};

const getServiceWorkerState = (): Record<string, unknown> => {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return { supported: false };

  const controller = navigator.serviceWorker.controller;
  return {
    supported: true,
    controlled: Boolean(controller),
    ...(controller?.scriptURL ? { scriptUrl: toRelativeUrl(controller.scriptURL) } : {}),
  };
};

const getRoute = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  return normalizeAppBrokenRoute(window.location);
};

/**
 * Everything known about the environment at the moment an asset load failed. Pure reads of
 * state the browser already holds — no extra requests, so it is safe on an error path.
 */
export const collectAssetLoadContext = (
  classification: AssetLoadErrorClassification,
): Record<string, unknown> | undefined => {
  if (typeof window === "undefined") return undefined;

  try {
    const recorded = getRecordedFailedAssets();

    return {
      kind: classification.kind,
      ...(classification.mimeType ? { mimeType: classification.mimeType } : {}),
      // Populated by the capture-phase listener below; empty when the browser rejected the
      // response without firing a resource error event (the dynamic-import case).
      failedAssets: recorded,
      recentAssetRequests: getRecentAssetTimings(),
      entryModuleUrl: getEntryModuleUrl(),
      route: getRoute(),
      documentPath: `${window.location.pathname}${window.location.hash}`,
      referrer: typeof document === "undefined" ? undefined : toRelativeUrl(document.referrer || ""),
      appVersion: (window as Window & { appVersion?: string }).appVersion,
      buildNumber: (window as Window & { buildNumber?: string }).buildNumber,
      serviceWorker: getServiceWorkerState(),
      online: typeof navigator === "undefined" ? undefined : navigator.onLine,
      msSincePageStart: now(),
    };
    // eslint-disable-next-line no-catch-all/no-catch-all -- diagnostics must never throw out of beforeSend
  } catch {
    return undefined;
  }
};

const firstExceptionMessage = (event: ErrorEvent, hint?: EventHint): string | undefined => {
  const values = event.exception?.values;
  for (const value of values ?? []) {
    const message = [value.type, value.value].filter(Boolean).join(": ");
    if (message) return message;
  }

  const original = hint?.originalException;
  if (original instanceof Error) return [original.name, original.message].filter(Boolean).join(": ");
  if (typeof original === "string") return original;

  return event.message;
};

export const UNKNOWN_ASSET_NAME = "unknown-asset";

// Vite names chunks `<name>-<8-char base64url hash>.<ext>`.
const HASHED_FILENAME = /^(.+)-[A-Za-z0-9_-]{8}(\.[A-Za-z0-9]+)$/;

/** `/assets/story-DEADBEEF.js?v=1` -> `story.js`: stable across deploys, so it can group. */
export const toAssetName = (url: string): string => {
  const path = url.split(/[?#]/)[0] ?? "";
  const filename = path.split("/").pop() ?? "";
  if (!filename) return UNKNOWN_ASSET_NAME;

  const match = HASHED_FILENAME.exec(filename);
  return match ? `${match[1]}${match[2]}` : filename;
};

/**
 * Best available name for the asset behind a MIME-type failure: the last element that fired a
 * resource error, else the lazy route module tagged on the error, else a placeholder.
 */
export const getFailingAssetName = (hint?: EventHint): string => {
  const lastFailed = failedAssets[failedAssets.length - 1];
  if (lastFailed) return toAssetName(lastFailed.url);

  const original = hint?.originalException as { tags?: Record<string, unknown> } | undefined;
  const routeModule = original && typeof original === "object" ? original.tags?.route_module : undefined;
  if (typeof routeModule === "string" && routeModule) return routeModule;

  return UNKNOWN_ASSET_NAME;
};

/**
 * Name the failing asset on MIME-type failures so they group per asset and the title says
 * which one. Other kinds already have server-side fingerprint rules, so they are left alone,
 * as is any fingerprint a capture site already chose.
 */
const nameFailingAsset = (event: ErrorEvent, classification: AssetLoadErrorClassification, hint?: EventHint): void => {
  if (classification.kind !== "invalid_mime_type" || event.fingerprint?.length) return;

  const assetName = getFailingAssetName(hint);
  event.fingerprint = ["asset-load-invalid-mime-type", classification.mimeType ?? "unknown", assetName];

  const exception = event.exception?.values?.find(
    (value) =>
      classifyAssetLoadError([value.type, value.value].filter(Boolean).join(": "))?.kind === classification.kind,
  );
  if (exception?.value) exception.value = `${exception.value} (asset: ${assetName})`;
};

/**
 * `beforeSend` hook that attaches asset-load diagnostics to events whose message says an asset
 * load failed, and leaves every other event byte-for-byte alone.
 *
 * MIME-type failures also get a per-asset fingerprint and the asset name in their message (see
 * `nameFailingAsset`); other asset-load kinds keep their existing grouping.
 */
export const assetLoadErrorBeforeSend = (event: ErrorEvent, hint?: EventHint): ErrorEvent => {
  const classification = classifyAssetLoadError(firstExceptionMessage(event, hint));
  if (!classification) return event;

  const route = getRoute();

  event.tags = {
    ...event.tags,
    [ASSET_LOAD_ERROR_TAG]: classification.kind,
    ...(classification.mimeType ? { [ASSET_LOAD_MIME_TYPE_TAG]: classification.mimeType } : {}),
    ...(route ? { [ASSET_LOAD_ROUTE_TAG]: route } : {}),
  };

  const context = collectAssetLoadContext(classification);
  if (context) {
    event.contexts = { ...event.contexts, [ASSET_LOAD_CONTEXT_KEY]: context };
  }

  nameFailingAsset(event, classification, hint);

  return event;
};

export type AddBreadcrumb = (breadcrumb: Breadcrumb) => void;

const breadcrumbForFailedAsset = (asset: FailedAsset): Breadcrumb => ({
  category: ASSET_LOAD_BREADCRUMB_CATEGORY,
  level: "error",
  message: `${asset.source} failed to load: ${asset.url}`,
  data: { ...asset },
});

const describeElement = (target: EventTarget | null): Omit<FailedAsset, "atMs"> | undefined => {
  if (typeof HTMLScriptElement !== "undefined" && target instanceof HTMLScriptElement) {
    return { url: target.src, source: "script", ...(target.type ? { detail: target.type } : {}) };
  }

  if (typeof HTMLLinkElement !== "undefined" && target instanceof HTMLLinkElement) {
    return { url: target.href, source: "link", ...(target.rel ? { detail: target.rel } : {}) };
  }

  return undefined;
};

let installed = false;

/**
 * Start recording asset-load failures. Safe to call before Sentry has loaded when given the
 * queueing `addBreadcrumb` from `./init`, whose calls are replayed once the SDK is in place.
 * It is injected rather than imported so this module does not import `./init`, which imports it.
 *
 * A resource `error` event does not bubble, so the listener has to be registered in the capture
 * phase — this is why nothing in the app saw these failures before.
 */
export const installAssetLoadTelemetry = ({ addBreadcrumb }: { addBreadcrumb: AddBreadcrumb }): void => {
  if (typeof window === "undefined" || installed) return;
  installed = true;

  window.addEventListener(
    "error",
    (event) => {
      const described = describeElement(event.target);
      // A normal JS error also arrives here; only element-level resource failures are ours.
      if (!described || !described.url) return;

      addBreadcrumb(breadcrumbForFailedAsset(recordFailedAsset(described)));
    },
    true,
  );

  window.addEventListener("vite:preloadError", (event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    const message = payload instanceof Error ? payload.message : String(payload ?? "vite:preloadError");

    addBreadcrumb({
      category: ASSET_LOAD_BREADCRUMB_CATEGORY,
      level: "error",
      message: `vite:preloadError ${message}`,
      data: { source: "preload-helper", atMs: now() },
    });
  });
};

/** Test seam: `installAssetLoadTelemetry` is a once-per-page side effect. */
export const resetAssetLoadTelemetryForTests = (): void => {
  installed = false;
  resetRecordedFailedAssets();
};
